import { FC } from 'react';

import { EAssetType } from 'constants/enums';
import {
  DEFAULT_BORDER_COLOR,
  GRADIENT_INPUT_STYLE,
  PROGRESS_BAR_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormikProps } from 'views/OfferDesign/OfferDesign.types';
import { getFieldError, getInputErrorProps } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

type ProgressBarFormFieldsProps = OfferDesignFormikProps & {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
};

export const ProgressBarFormFields: FC<ProgressBarFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique
}) => {
  const uniqueFlags = { isNameUnique, isExternalIdUnique };
  const nameError = getFieldError('name', touched, errors, uniqueFlags, 'isNameUnique');
  const externalIdError = getFieldError('externalId', touched, errors, uniqueFlags, 'isExternalIdUnique');
  const backgroundOpacityError = touched.backgroundOpacity ? errors.backgroundOpacity : undefined;
  const frameRadiusError = touched.frameRadius ? errors.frameRadius : undefined;
  const accumulatedImageError = touched.accumulatedItemImageUrl ? errors.accumulatedItemImageUrl : undefined;

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Progress Bar</h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the progress bar to fit your store theme.
      </p>

      <div className="flex gap-5">
        <Input
          label="Name"
          isRequired
          name="name"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(nameError)}
          className="flex-1"
        />
        <Input
          label="External ID"
          isRequired
          name="externalId"
          placeholder="External ID"
          value={values.externalId}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(externalIdError)}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Progress Bar Frame
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Set your frame settings.
      </p>

      <div className="flex gap-5">
        <Input
          label="Background Opacity"
          isRequired
          name="backgroundOpacity"
          type="number"
          min={0}
          max={100}
          value={values.backgroundOpacity}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(backgroundOpacityError)}
          leftIcon={<span>%</span>}
          leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
          className="flex-1"
        />
        <Input
          label="Frame Radius"
          isRequired
          name="frameRadius"
          type="number"
          min={0}
          value={values.frameRadius}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(frameRadiusError)}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Progress Bar Design
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Set colors and upload assets for your bar.
      </p>

      <div className="flex gap-5">
        <div className="flex-1">
          <ColorPicker
            label="Font Color"
            defaultValue={values.textColor || PROGRESS_BAR_DEFAULTS.textColor}
            onChange={(color) => setFieldValue('textColor', color)}
          />
        </div>
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Bar Fill Color"
            name="barFillColor"
            defaultValue={
              typeof values.barFillColor === 'object' && values.barFillColor
                ? values.barFillColor
                : { colorOne: values.barFillColor || PROGRESS_BAR_DEFAULTS.barFillColor.colorOne }
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
      </div>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className="flex gap-5 mt-4">
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Border Color"
            name="borderColor"
            defaultValue={
              typeof values.borderColor === 'object' && values.borderColor
                ? values.borderColor
                : DEFAULT_BORDER_COLOR
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Background Color"
            name="barBackgroundColor"
            defaultValue={
              typeof values.barBackgroundColor === 'object' && values.barBackgroundColor
                ? values.barBackgroundColor
                : PROGRESS_BAR_DEFAULTS.backgroundColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
      </div>

      <div className="flex gap-5 mt-4">
        <Input
          label="Bar Radius"
          name="barRadius"
          type="number"
          min={0}
          value={values.barRadius}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1"
        />
        <Input
          label="Border Width"
          name="borderWidth"
          type="number"
          min={0}
          value={values.borderWidth}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1"
        />
      </div>

      <div className="mt-9">
        <UploadImage
          title="Accumulated Image"
          isRequired
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('accumulatedItemImageUrl', url)}
          selectedImageUrl={values.accumulatedItemImageUrl}
          forceType={EAssetType.BG_BUNDLE}
          hasError={!!accumulatedImageError}
          errorMessage={accumulatedImageError}
          TooltipContent={() => (
            <span>Upload an accumulated item image for the progress bar</span>
          )}
        />
      </div>
    </div>
  );
};
